/**
 * Binds a Yjs document to an {@link UpdateLog}.
 *
 * This is the only module in the repo that knows the bytes in a log entry
 * are Yjs updates. Everything above it sees a `Y.Text` (the editor, via
 * `y-codemirror.next`) or a plain string (`toString` / `setText`, for
 * writers that have no CRDT of their own, such as the FUSE mirror);
 * everything below it sees opaque binary blobs.
 *
 * Why an update log rather than a string column: a string column forces
 * whole-document writes, and a whole-document write computed against a stale
 * view silently erases whatever the other writer did in the meantime. Yjs
 * updates are commutative and immutable, so two writers append two entries
 * and both edits survive in either application order — no merge policy, and
 * nothing for the backend to get wrong.
 */
import * as Y from "yjs";
import type { UpdateLog, Unsubscribe } from "./update-log.js";

/** Yjs map key the shared text lives under. */
const DEFAULT_TEXT_KEY = "content";

/**
 * Transaction origin marking "these bytes came from the log", so the update
 * handler does not append them straight back.
 */
const FROM_LOG = Symbol("collab-text/from-log");

/** Origin marking a write this process made itself. */
const LOCAL = Symbol("collab-text/local");

export interface CollaborativeDocumentOptions {
  /** Key of the shared text within the Yjs document. Defaults to `content`. */
  textKey?: string;
  /**
   * Called when an append is rejected or fails. Hosts that can surface the
   * failure to the user should pass one; the default only logs.
   */
  onAppendError?: (error: unknown) => void;
  /**
   * Called when a log entry cannot be applied. Defaults to `onAppendError`,
   * since both mean "the log and the document disagree and a human should
   * know"; hosts that want to distinguish them can pass this separately.
   */
  onEntryError?: (error: unknown) => void;
}

/**
 * Default append-failure handler.
 *
 * A failed append means an edit the user already sees on screen was not
 * persisted, so it must be loud. It deliberately does not throw: the caller
 * is a Yjs update handler, and an exception there would leave the document
 * and the log inconsistent for every subsequent edit rather than just this
 * one.
 */
function reportAppendFailure(error: unknown): void {
  console.error("collab-text: failed to persist an update", error);
}

/**
 * Where a `setText` call has to splice to turn `current` into `next`.
 *
 * Deliberately the cheapest useful diff — a common prefix and a common
 * suffix around one replaced middle. That is exact for the edits this
 * actually sees (a text editor writing a file back), and being approximate
 * for anything else costs merge quality, never correctness: the CRDT still
 * converges, a bigger replaced range just means less of a concurrent edit
 * inside it survives.
 */
export interface TextSplice {
  index: number;
  deleteCount: number;
  insert: string;
}

/** Computes the splice turning `current` into `next`, or null if equal. */
export function textSplice(current: string, next: string): TextSplice | null {
  if (current === next) return null;

  const limit = Math.min(current.length, next.length);
  let head = 0;
  while (head < limit && current[head] === next[head]) head++;

  let currentTail = current.length;
  let nextTail = next.length;
  while (
    currentTail > head &&
    nextTail > head &&
    current[currentTail - 1] === next[nextTail - 1]
  ) {
    currentTail--;
    nextTail--;
  }

  return {
    index: head,
    deleteCount: currentTail - head,
    insert: next.slice(head, nextTail),
  };
}

/**
 * Encodes `text` as a single update that materialises to exactly that text.
 *
 * This is how a document that already exists as a plain string enters the
 * log — a legacy file whose content predates collaborative text, or a file
 * created with initial content. Appending it as the log's first (snapshot)
 * entry is what makes the migration lazy: nothing is rewritten until someone
 * opens the file.
 *
 * Seeding must happen exactly once per document. Two writers each seeding
 * the same text produce two different sets of Yjs client ids, and applying
 * both concatenates the text rather than merging it — so the *log* must be
 * created atomically by the backend, not the seed deduplicated afterwards.
 */
export function encodeInitialText(
  text: string,
  options: { textKey?: string } = {},
): Uint8Array {
  const doc = new Y.Doc();
  doc.getText(options.textKey ?? DEFAULT_TEXT_KEY).insert(0, text);
  const update = Y.encodeStateAsUpdate(doc);
  doc.destroy();
  return update;
}

/** A document's whole state, with the log entries it supersedes. */
export interface DocumentSnapshot {
  /** The state encoded as a single update. */
  update: Uint8Array;
  /**
   * Ids of the log entries whose content `update` is known to contain —
   * exactly the entries this document had applied when it was encoded.
   */
  containedEntryIds: string[];
}

export class CollaborativeDocument {
  readonly doc: Y.Doc;
  readonly text: Y.Text;

  private readonly appliedEntryIds = new Set<string>();
  private readonly changeCallbacks = new Set<(text: string) => void>();
  private readonly localChangeCallbacks = new Set<() => void>();
  private readonly unsubscribeLog: Unsubscribe;
  private readonly onAppendError: (error: unknown) => void;
  private readonly onEntryError: (error: unknown) => void;
  private disposed = false;

  constructor(
    private readonly log: UpdateLog,
    options: CollaborativeDocumentOptions = {},
  ) {
    this.onAppendError = options.onAppendError ?? reportAppendFailure;
    this.onEntryError = options.onEntryError ?? this.onAppendError;

    this.doc = new Y.Doc();
    this.text = this.doc.getText(options.textKey ?? DEFAULT_TEXT_KEY);

    this.drainLog();

    this.doc.on("update", this.handleDocUpdate);
    this.text.observe(this.handleTextChange);
    this.unsubscribeLog = this.log.subscribe(() => {
      this.drainLog();
    });
  }

  /**
   * The document materialised as a plain string.
   *
   * `Y.Text.toJSON()` rather than `toString()` throughout: both return the
   * same string at runtime, but yjs' generated `.d.ts` only declares
   * `toJSON`, so `toString` reads as `Object.prototype.toString` to the type
   * checker.
   */
  toString(): string {
    return this.text.toJSON();
  }

  /**
   * Replace the whole content, expressed as the minimal splice against the
   * current state. For writers that hand over a finished string rather than
   * edit operations — the FUSE mirror, a paste-over-everything.
   *
   * Unlike a whole-document write to a string column, this is still a CRDT
   * operation: characters outside the replaced range are untouched, so a
   * concurrent edit elsewhere in the document survives.
   */
  setText(next: string): void {
    const splice = textSplice(this.text.toJSON(), next);
    if (!splice) return;
    this.doc.transact(() => {
      if (splice.deleteCount > 0) {
        this.text.delete(splice.index, splice.deleteCount);
      }
      if (splice.insert.length > 0) {
        this.text.insert(splice.index, splice.insert);
      }
    }, LOCAL);
  }

  /** Notified with the materialised text whenever it changes, from any source. */
  onChange(callback: (text: string) => void): Unsubscribe {
    this.changeCallbacks.add(callback);
    return () => {
      this.changeCallbacks.delete(callback);
    };
  }

  /**
   * Notified when *this* process changed the document, as opposed to
   * replaying someone else's change.
   *
   * The distinction matters to anything that writes derived state back to
   * the backend. Every peer converges on the same text, so if every peer
   * also wrote the derived copy, one person's keystroke would cost a write
   * per connected client. Whoever made the change does the work.
   */
  onLocalChange(callback: () => void): Unsubscribe {
    this.localChangeCallbacks.add(callback);
    return () => {
      this.localChangeCallbacks.delete(callback);
    };
  }

  /** Detach from the log and destroy the Yjs document. */
  destroy(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.unsubscribeLog();
    this.doc.off("update", this.handleDocUpdate);
    this.text.unobserve(this.handleTextChange);
    this.changeCallbacks.clear();
    this.localChangeCallbacks.clear();
    this.doc.destroy();
  }

  /**
   * The document's whole state as one update, plus the log entries that
   * update is known to contain.
   *
   * The id list is what makes compaction safe: those entries are provably
   * superseded, because this document applied every one of them before the
   * state was encoded. Deducing the same thing from timestamps or list
   * order would be a guess, and guessing wrong deletes an edit.
   */
  encodeSnapshot(): DocumentSnapshot {
    return {
      update: Y.encodeStateAsUpdate(this.doc),
      containedEntryIds: [...this.appliedEntryIds],
    };
  }

  /** Apply every log entry not yet seen. Yjs updates are idempotent. */
  private drainLog(): void {
    if (this.disposed) return;
    const entries = this.log.getEntries();
    const fresh = entries.filter(
      (entry) => !this.appliedEntryIds.has(entry.id),
    );

    // Forget ids compaction removed. Without this the set grows for the
    // life of the document, and `encodeSnapshot` reports entries that are
    // already gone. Re-delivery of a forgotten entry is harmless — applying
    // a Yjs update twice changes nothing.
    if (entries.length - fresh.length < this.appliedEntryIds.size) {
      const present = new Set(entries.map((entry) => entry.id));
      for (const id of this.appliedEntryIds) {
        if (!present.has(id)) this.appliedEntryIds.delete(id);
      }
    }

    if (fresh.length === 0) return;
    // An entry is recorded as applied only once it has been, and one at a
    // time. Marking the whole batch up front means a single unreadable entry
    // — a truncated payload, a bit flip, an encoder this build predates —
    // marks every entry behind it in the batch too, and none of them is ever
    // retried. That is not merely staleness: the applied set is what
    // `encodeSnapshot` reports as contained, so an entry marked but never
    // applied becomes a snapshot claiming text it does not carry, and
    // compaction then deletes the original. Skipping just the bad entry
    // leaves the rest correct and lets the entry be retried if it is ever
    // readable.
    this.doc.transact(() => {
      for (const entry of fresh) {
        try {
          Y.applyUpdate(this.doc, entry.update, FROM_LOG);
        } catch (error) {
          this.onEntryError(error);
          continue;
        }
        this.appliedEntryIds.add(entry.id);
      }
    }, FROM_LOG);
  }

  private readonly handleDocUpdate = (
    update: Uint8Array,
    origin: unknown,
  ): void => {
    if (origin === FROM_LOG) return;
    // `.catch` alone is not enough: a backend that does real work before
    // returning its promise — creating a CoValue, pushing to a list — can
    // throw synchronously, and that exception would escape a Yjs update
    // handler mid-transaction. Route it to the same place as a rejection.
    try {
      this.log.append(update).catch(this.onAppendError);
    } catch (error) {
      this.onAppendError(error);
    }
    for (const callback of [...this.localChangeCallbacks]) callback();
  };

  private readonly handleTextChange = (): void => {
    if (this.changeCallbacks.size === 0) return;
    const materialised = this.text.toJSON();
    for (const callback of [...this.changeCallbacks]) callback(materialised);
  };
}
